import type { FC } from "react"
import {
  Button,
  Flex,
  Icon,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Spinner,
  Stack,
  Text,
} from "@chakra-ui/react"
import invariant from "tiny-invariant"

import ErrorBoundary from "common/components/ErrorBoundary"
import { FeatureFlag, useGovernanceContext } from "governance/context"
import Web3Button from "web3/components/Web3Button"
import type {
  Governor,
  ProposalActionAttemptQuery,
  ProposalActionAttemptQueryVariables,
  Proposal,
} from "query/graphql"
import {
  ProposalActionAttemptDocument,
  ProposalActionType,
  ProposalStatus,
  useProposalQueuedCtaQuery,
} from "query/graphql"
import { useWithProposalErrorCatch } from "governance/hooks/useWithProposalErrorCatch"
import ProposalShareButton from "proposal/components/ctas/ProposalShareButton"
import ProposalExecuteAdvancedButton from "proposal/components/ctas/ProposalExecuteAdvancedButton"
import ProposalBlockExplorerURLButton from "proposal/components/ctas/ProposalBlockExplorerURLButton"
import ProposalCancelButton from "proposal/components/ctas/ProposalCancelButton"
import ProposalLearnMoreAboutButton from "proposal/components/ctas/ProposalLearnMoreAboutButton"
import { useExecuteProposal } from "proposal/hooks/useExecuteProposal"
import { useQuery } from "query/hooks/useQuery"
import { EllipsisVertical } from "ui/components/icons/font-awesome/EllipsisVertical"
import { EXTERNAL_ROUTES, ROUTES } from "common/constants/routes"
import { GrpcErrorType } from "common/helpers/fetcher"
import ShareToFarcasterButton from "common/components/ShareToFarcasterButton"
import { useDescriptionException } from "proposal/hooks/useDescriptionException"
import { useOrganization } from "organization/providers/OrganizationProvider"
import ShareToLensButton from "common/components/ShareToLensButton"
import { Chain } from "web3/hooks/useMultichainAccount"

type ProposalQueuedCTAProps = {
  proposalId: string
  slug: string
}
const ProposalQueuedCTA: FC<ProposalQueuedCTAProps> = ({
  proposalId,
  slug,
}) => {
  const { isFeatureFlagOn } = useGovernanceContext()
  const displayExecuteBtnOnActiveFlag = isFeatureFlagOn(
    FeatureFlag.DisplayExecuteBtnOnActiveStatus,
  )

  const { data, isLoading } = useProposalQueuedCtaQuery({
    input: { id: proposalId },
  })

  if (isLoading) return null

  if (!data) return null

  const { proposal } = data

  invariant(proposal.governor, "governor is required")

  const { timelockId } = proposal.governor
  const hasTimelock = Boolean(timelockId)

  const isExecuteDisabled = hasTimelock
    ? proposal.status !== ProposalStatus.Queued &&
      proposal.status !== ProposalStatus.Pendingexecution
    : proposal.status !== ProposalStatus.Succeeded

  if (isExecuteDisabled && !displayExecuteBtnOnActiveFlag) return null

  return (
    <ExecuteButton
      governance={proposal.governor as Governor}
      proposal={proposal as Proposal}
      slug={slug}
    />
  )
}

type ExecuteButtonProps = {
  proposal: Proposal
  governance: Governor
  slug: string
}
const ExecuteButton: FC<ExecuteButtonProps> = ({
  proposal,
  governance,
  slug,
}) => {
  const { executeProposal } = useExecuteProposal({ proposal, governance })
  const { withErrorCatch } = useWithProposalErrorCatch()

  const handleExecute = async (): Promise<void> => {
    await withErrorCatch(executeProposal)
  }

  const { data: proposalActionAttemptData } = useQuery<
    ProposalActionAttemptQuery,
    ProposalActionAttemptQueryVariables
  >({
    query: ProposalActionAttemptDocument,
    variables: {
      input: {
        proposalId: proposal.id,
        type: ProposalActionType.Execute,
      },
    },
    omittedErrors: [GrpcErrorType.NotFound],
  })
  const { data: proposalActionAttemptCancelData } = useQuery<
    ProposalActionAttemptQuery,
    ProposalActionAttemptQueryVariables
  >({
    query: ProposalActionAttemptDocument,
    variables: {
      input: {
        proposalId: proposal.id,
        type: ProposalActionType.Cancel,
      },
    },
    omittedErrors: [GrpcErrorType.NotFound],
  })

  if (proposalActionAttemptData || proposalActionAttemptCancelData) {
    const proposalActionAttempt = proposalActionAttemptData
      ? proposalActionAttemptData.proposalActionAttempt
      : proposalActionAttemptCancelData?.proposalActionAttempt

    return (
      <Stack
        align="center"
        color="gray.600"
        direction="row"
        justify={{ base: "center", lg: "right" }}
        spacing={4}
        width="100%"
      >
        <Stack
          align="center"
          direction="row"
          spacing={3}
          width={{ base: "100%", sm: "auto" }}
        >
          <Spinner />
          {proposalActionAttemptData ? (
            <Text textStyle="body.bold.lg">Executing Proposal</Text>
          ) : null}
          {proposalActionAttemptCancelData ? (
            <Text textStyle="body.bold.lg">Canceling Proposal</Text>
          ) : null}
        </Stack>

        <OptionsList
          governance={governance}
          proposal={proposal}
          proposalActionAttempt={proposalActionAttempt}
          slug={slug}
        />
      </Stack>
    )
  }

  return (
    <ErrorBoundary section="ProposalExecuteButton">
      <Stack
        align="center"
        direction="row"
        justify={{ base: "center", lg: "right" }}
        spacing={4}
        width="100%"
      >
        <Web3Button
          px={10}
          requiredChainId={governance.chainId}
          variant="primary"
          walletType={Chain.EVM}
          width={{ base: "100%", sm: "auto" }}
          onValid={handleExecute}
        >
          Execute
        </Web3Button>

        <OptionsList governance={governance} proposal={proposal} slug={slug} />
      </Stack>
    </ErrorBoundary>
  )
}

type OptionsListProps = {
  governance: Governor
  proposal: Proposal
  proposalActionAttempt?: ProposalActionAttemptQuery["proposalActionAttempt"]
  slug: string
}
const OptionsList: FC<OptionsListProps> = ({
  governance,
  proposal,
  proposalActionAttempt,
  slug,
}) => {
  const { title } = useDescriptionException({
    governanceId: governance.id,
    proposal,
  })
  const { isFeatureFlagOn } = useGovernanceContext()
  const hideCancelProposal = isFeatureFlagOn(FeatureFlag.HideCancelProposal)

  const { organization } = useOrganization()

  // const getProposalTransactionParams = () => {
  //   const txHash = getGovernorProposalTxHash(proposal)

  //   if (txHash) {
  //     return { txHash, chainId: governance.chainId }
  //   }

  //   if (proposalActionAttempt?.tx) {
  //     return getTransactionParams(proposalActionAttempt.txID)
  //   }

  //   return undefined
  // }

  // const transactionParams = getProposalTransactionParams()

  return (
    <Flex>
      <Menu>
        <MenuButton
          as={Button}
          bgColor="white"
          borderColor="gray.300"
          borderWidth="1px"
          px={3}
        >
          <Icon as={EllipsisVertical} fill="gray.600" />
        </MenuButton>
        <MenuList>
          {organization && proposal?.onchainId ? (
            <MenuItem>
              <ProposalShareButton
                url={ROUTES.governance.proposal.id(
                  slug,
                  proposal.onchainId,
                  organization.governorIds.length && proposal.governor
                    ? proposal.governor.id
                    : "",
                )}
              />
            </MenuItem>
          ) : null}

          {organization && title ? (
            <>
              <MenuItem>
                <ShareToFarcasterButton
                  size="sm"
                  title={`Tally | ${organization.name} | ${title}`}
                  url={ROUTES.governance.proposal.id(
                    organization.slug,
                    proposal.id,
                    organization.governorIds.length && proposal.governor
                      ? proposal.governor.id
                      : "",
                  )}
                  variant="tertiary"
                />
              </MenuItem>
              <MenuItem>
                <ShareToLensButton
                  size="sm"
                  title={`Tally | ${organization.name} | ${title}`}
                  url={ROUTES.governance.proposal.id(
                    organization.slug,
                    proposal.id,
                    organization.governorIds.length && proposal.governor
                      ? proposal.governor.id
                      : "",
                  )}
                  variant="tertiary"
                />
              </MenuItem>
            </>
          ) : null}

          <MenuItem>
            <ProposalExecuteAdvancedButton
              governance={governance}
              proposal={proposal}
            />
          </MenuItem>
          {proposalActionAttempt?.txHash && proposalActionAttempt?.chainId ? (
            <MenuItem>
              <ProposalBlockExplorerURLButton
                chainId={proposalActionAttempt.chainId}
                txHash={proposalActionAttempt.txHash}
              />
            </MenuItem>
          ) : null}
          {!hideCancelProposal ? (
            <MenuItem>
              <ProposalCancelButton
                governance={governance}
                proposal={proposal}
              />
            </MenuItem>
          ) : null}
          <MenuItem>
            <ProposalLearnMoreAboutButton
              label={`Learn more about "Execute"`}
              url={EXTERNAL_ROUTES.tally.docs.executingProposals()}
            />
          </MenuItem>
        </MenuList>
      </Menu>
    </Flex>
  )
}

export default ProposalQueuedCTA
